import React, { useState } from "react";
import { Box } from "@mui/material";
import Modal, { ModalHead, ModalBody, ModalFoot } from "../../../../../component/Modal";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import FormField from "../../../../../component/Formfield";
import { useNotify } from "../../../../../component/NotificationProvider";
import { getOrgShifts } from "../../../../../services/globalData";
import { createAttendanceRequest } from "../../../../../services/attendanceRequest";
import { apiError } from "../../../../../utils/formErrors";

/**
 * Asking to be moved to a different shift.
 *
 * This used to live inside a side panel that only appeared once a day had been
 * clicked on the old attendance calendar — which meant asking to change your
 * shift required first selecting a date the request has nothing to do with. It
 * is a standing request about your roster, not about a day, so it is now a
 * modal raised from the requests list where its answer will appear.
 *
 * Errors land on the field that caused them rather than in a toast: a rejected
 * request that says only "failed" leaves somebody to guess which of three
 * inputs was wrong. `validation` is what FormField reads — it draws both the
 * danger outline and the line underneath, so passing `error` here would have
 * silently done neither.
 */

const ShiftChangeForm = ({ open, onClose, onSubmitted }) => {
  const notify = useNotify();
  const shiftOptions = (getOrgShifts() || []).map((shift) => ({
    _id: shift?._id,
    value: shift?._id,
    name: shift?.name,
  }));

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ requestedShift: "", requestedLocation: "", reason: "" });
  const [errors, setErrors] = useState({});

  const change = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const close = () => {
    setForm({ requestedShift: "", requestedLocation: "", reason: "" });
    setErrors({});
    onClose?.();
  };

  const handleSubmit = async () => {
    const next = {};
    if (!form.requestedShift) next.requestedShift = "Choose the shift you want to move to.";
    if (!form.reason.trim()) next.reason = "Say why, so your manager can decide.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      const payload = {
        type: "SHIFT_CHANGE",
        requestedShift: form.requestedShift,
        reason: form.reason.trim(),
      };
      if (form.requestedLocation) payload.requestedLocation = form.requestedLocation;

      const res = await createAttendanceRequest(payload);
      if (res?.success === false) throw apiError(res, "Failed to submit request");
      notify.success("Shift change request submitted");
      onSubmitted?.();
      close();
    } catch (error) {
      notify.error(error?.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={close} width="34rem">
      <ModalHead
        title="Request a shift change"
        subtitle="Your manager decides this one; nothing changes until they approve it."
        onClose={close}
      />
      <ModalBody>
        <Box>
          <FormField
            label="Requested shift"
            type="select"
            options={shiftOptions}
            value={form.requestedShift}
            onChange={(e) => change("requestedShift", e.target.value)}
            fullwidth
            validation={errors.requestedShift}
          />
          <FormField
            label="Where you would work it"
            type="select"
            options={["On Site", "Remote"]}
            value={form.requestedLocation}
            onChange={(e) => change("requestedLocation", e.target.value)}
            fullwidth
          />
          <FormField
            label="Reason"
            type="text"
            value={form.reason}
            onChange={(e) => change("reason", e.target.value)}
            fullwidth
            row={4}
            validation={errors.reason}
            noMargin
          />
        </Box>
      </ModalBody>
      <ModalFoot note="You will see the decision in your requests below.">
        <BtnSecondary onClick={close} sx={{ borderRadius: "8px" }}>
          Discard
        </BtnSecondary>
        <BtnPrimary onClick={handleSubmit} disabled={submitting} sx={{ borderRadius: "8px" }}>
          {submitting ? "Sending…" : "Send request"}
        </BtnPrimary>
      </ModalFoot>
    </Modal>
  );
};

export default ShiftChangeForm;
